// The supervisor channel: what an agent process and the program that started
// it (the desktop app, or the launcher on Linux) say to each other. It rides
// on node's IPC (Electron's utilityProcess `parentPort`, or child_process
// `fork`), next to nothing else, one message per object.
//
// The supervisor learns the agent's state from these messages, never from log
// lines. Exit codes stay too (systemd reads them): their meanings are the
// AGENT_EXIT table (supervisor-constants.ts), the only place they are defined.
import { z } from "zod";
import { AgentKind, Capabilities, RateLimits } from "./capabilities";

export {
  AGENT_EXIT,
  type AgentExitCode,
  LAUNCHER_ENV,
  SUPERVISOR_ENV,
} from "./supervisor-constants";

export const AgentStatus = z.discriminatedUnion("state", [
  z.object({ state: z.literal("starting") }),
  z.object({
    state: z.literal("connected"),
    deviceId: z.string().max(100),
    deviceName: z.string().max(200),
  }),
  z.object({
    state: z.literal("reconnecting"),
    /** A short code (`closed`, `refused`, `network`), not a sentence. */
    reason: z.string().max(100),
    retryAt: z.string().max(40).nullable(),
  }),
]);
export type AgentStatus = z.infer<typeof AgentStatus>;

/** One agent's usage limits, keyed by CLI. */
export const UsageSnapshot = z.object({
  limits: z.array(z.object({ kind: AgentKind, rateLimits: RateLimits })),
});
export type UsageSnapshot = z.infer<typeof UsageSnapshot>;

/** Agent → supervisor. */
export const AgentToSupervisor = z.discriminatedUnion("t", [
  z.object({ t: z.literal("status"), status: AgentStatus }),
  z.object({ t: z.literal("capabilities"), capabilities: Capabilities }),
  /** The first accepted hello of this process (stops the crash count of a new version). */
  z.object({ t: z.literal("hello_ok"), version: z.string().max(100) }),
  z.object({
    t: z.literal("exiting"),
    code: z.number().int(),
    message: z.string().max(500),
  }),
  z.object({ t: z.literal("live_check_done"), ok: z.boolean() }),
]);
export type AgentToSupervisor = z.infer<typeof AgentToSupervisor>;

/** Supervisor → agent. */
export const SupervisorToAgent = z.discriminatedUnion("t", [
  /** Usage fetched by the supervisor; once one arrives the agent stops fetching its own. */
  z.object({ t: z.literal("usage"), usage: UsageSnapshot }),
  z.object({ t: z.literal("live_check") }),
  /** Stop running work, then exit with 0. */
  z.object({ t: z.literal("stop") }),
]);
export type SupervisorToAgent = z.infer<typeof SupervisorToAgent>;

/** The IPC endpoint as both sides see it (`process` in the child, the child handle in the parent). */
export interface SupervisorPort {
  send(message: unknown): void;
  /** Returns a function that removes the listener. */
  onMessage(listener: (message: unknown) => void): () => void;
}

/** Parses what arrives; anything else on the same channel (AgentHost frames, foreign messages) is null. */
export function readAgentMessage(raw: unknown): AgentToSupervisor | null {
  const r = AgentToSupervisor.safeParse(raw);
  return r.success ? r.data : null;
}

export function readSupervisorMessage(raw: unknown): SupervisorToAgent | null {
  const r = SupervisorToAgent.safeParse(raw);
  return r.success ? r.data : null;
}
